import React from 'react'
import styled, { css } from 'styled-components'
import { Radio } from 'sutra/components/Radio/Radio'
import { RadioGroup } from 'sutra/components/Radio/RadioGroup'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export type SegmentedControlSize = 'small' | 'middle'

export interface SegmentedControlOption<T extends string | number> {
  label: React.ReactNode
  value: T
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string | number> {
  options: SegmentedControlOption<T>[]
  // The selected value; `undefined` or `null` means nothing is selected.
  value?: T | null
  onValueChange?: (value: T) => void
  // Stretch to the container's width, with every segment the same width.
  block?: boolean
  size?: SegmentedControlSize
  disabled?: boolean
  'aria-label'?: string
  'data-testid'?: string
  className?: string
}

const SMALL_HEIGHT = 24

// `styled()` erases the generic, so `onValueChange` is typed for the widest
// `T`; `SegmentedControl` narrows it back below.
const Group = styled(RadioGroup<string | number>)<{ $block: boolean }>`
  ${({ $block }) =>
    $block &&
    css`
      display: flex;
      width: 100%;
    `}
`

const Segment = styled(Radio)<{
  $size: SegmentedControlSize
  $block: boolean
}>`
  ${({ $size }) =>
    $size === 'small' &&
    css`
      min-height: ${SMALL_HEIGHT}px;
      padding: 0 ${spacing[2]}px;
      font-size: ${typography.text.xs.fontSize}px;
    `}
  ${({ $block }) =>
    $block &&
    css`
      flex: 1 1 0;
      min-width: 0;
    `}
`

// A single-select row of joined buttons. It is a `RadioGroup` underneath, so
// exactly one segment stays selected and the arrow keys move between them.
export const SegmentedControl = <T extends string | number>({
  options,
  value,
  onValueChange,
  block = false,
  size = 'middle',
  disabled,
  'aria-label': ariaLabel,
  'data-testid': testId,
  className,
}: SegmentedControlProps<T>): React.ReactElement => (
  <Group
    variant="button"
    // A controlled group must never see `undefined`, which makes it
    // uncontrolled.
    value={value ?? null}
    onValueChange={(next) => onValueChange?.(next as T)}
    disabled={disabled}
    aria-label={ariaLabel}
    data-testid={testId}
    className={className}
    $block={block}
  >
    {options.map((option) => (
      <Segment
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        $size={size}
        $block={block}
      >
        {option.label}
      </Segment>
    ))}
  </Group>
)

SegmentedControl.displayName = 'SegmentedControl'
